import { apiFetch } from '@/stores/authStore';
import type { FrozenCancelSnapshot } from './gameOrderSelection';

export const CANCEL_ALL_AUDIT_TIMEOUT_MS = 750;

/**
 * The scopes this breadcrumb can describe. Position Tracker is the only surface
 * that records one (see `surface` below), and it sweeps by filter, never by
 * ladder level — so the parameter EXCLUDES #523's `'level'` rather than letting
 * it fall through to `'all'`. A single price level logged as an account-wide
 * sweep is a falsified audit record; keeping it out of the type means a future
 * caller fails to compile instead of writing one.
 */
export type AuditableCancelSnapshot = FrozenCancelSnapshot & {
  scope: 'visible' | 'all' | 'game';
};

export function buildBulkCancelAttemptBody(
  snapshot: AuditableCancelSnapshot,
  attemptId: string = crypto.randomUUID(),
) {
  return {
    attemptId,
    snapshotId: snapshot.id,
    surface: 'position_tracker' as const,
    scope: snapshot.scope === 'visible' ? ('visible' as const) : ('all' as const),
    targets: snapshot.targets.map(({ venue, orderId }) => ({ venue, orderId })),
  };
}

/**
 * Best-effort evidence only. A short abort keeps audit degradation from
 * becoming a cancellation delay; callers surface the error and continue with
 * the frozen per-order requests.
 */
export async function recordBulkCancelAttempt(snapshot: FrozenCancelSnapshot): Promise<void> {
  // Fail closed rather than mislabel. This also narrows `snapshot` for the
  // builder below, so the guarantee is enforced at runtime AND checked at the
  // type level without narrowing this function's own parameter — it is passed
  // as `BulkCancelDeps.recordAttempt`, whose signature takes every scope.
  // The executor reports a thrown recordAttempt as `auditError` and still sends
  // the venue cancels, so this degrades the breadcrumb, never the cancellation.
  if (snapshot.scope === 'level') {
    throw new Error('A price-level cancel must never be recorded as an account-wide sweep');
  }
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timeoutFailure = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('Cancel All attempt log timed out'));
    }, CANCEL_ALL_AUDIT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([
      apiFetch('/api/kalshi/orders/cancel-all-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The guard above proves the scope; TS narrows the property, not the object.
        body: JSON.stringify(buildBulkCancelAttemptBody(snapshot as AuditableCancelSnapshot)),
        signal: controller.signal,
      }),
      timeoutFailure,
    ]);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        (body as Record<string, string>).error ?? `Cancel All attempt log failed (${res.status})`,
      );
    }
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}
